/**
 * Engine-specific property presets for tiles, objects, layers, and maps.
 * When a target engine is selected, these provide suggested properties
 * with correct names, types, and defaults for that engine.
 */

export const PROPERTY_PRESETS = {
  // Per-tile properties (on tileset tiles)
  tile: {
    phaser: [
      { name: 'collides', type: 'bool', default: false, description: 'Tile blocks movement (Arcade physics)' },
      { name: 'depth', type: 'int', default: 0, description: 'Render depth/order' },
      { name: 'cost', type: 'float', default: 1, description: 'Pathfinding movement cost' },
    ],
    godot: [
      { name: 'collision_layer', type: 'int', default: 1, description: 'Physics collision layer bitmask' },
      { name: 'collision_mask', type: 'int', default: 1, description: 'Physics collision mask bitmask' },
      { name: 'z_index', type: 'int', default: 0, description: 'Render order' },
      { name: 'y_sort', type: 'bool', default: false, description: 'Enable Y-sort rendering' },
      { name: 'navigation', type: 'bool', default: true, description: 'Tile is navigable' },
    ],
    unity: [
      { name: 'unity:layer', type: 'string', default: 'Default', description: 'Unity physics layer name' },
      { name: 'unity:tag', type: 'string', default: 'Untagged', description: 'Unity tag' },
      { name: 'unity:isTrigger', type: 'bool', default: false, description: 'Collider is a trigger volume' },
      { name: 'unity:SortingLayer', type: 'string', default: 'Default', description: 'Unity sorting layer' },
    ],
    gamemaker: [
      { name: 'solid', type: 'bool', default: false, description: 'Tile is solid (collision)' },
    ],
    rpgmaker: [
      { name: 'passable', type: 'bool', default: true, description: 'Tile can be walked on' },
      { name: 'passable_up', type: 'bool', default: true, description: 'Can pass moving up' },
      { name: 'passable_down', type: 'bool', default: true, description: 'Can pass moving down' },
      { name: 'passable_left', type: 'bool', default: true, description: 'Can pass moving left' },
      { name: 'passable_right', type: 'bool', default: true, description: 'Can pass moving right' },
      { name: 'terrain_tag', type: 'int', default: 0, description: 'Terrain tag for encounters' },
    ],
    generic: [
      { name: 'collision', type: 'bool', default: false, description: 'Tile blocks movement' },
      { name: 'walkable', type: 'bool', default: true, description: 'Tile can be walked on' },
    ],
  },

  // Object properties (on object layer objects)
  object: {
    phaser: [
      { name: 'type', type: 'string', default: '', description: 'Object type (spawn_point, trigger, portal, npc, enemy, item)' },
      { name: 'alpha', type: 'float', default: 1, description: 'Object opacity' },
      { name: 'depth', type: 'int', default: 0, description: 'Render depth' },
    ],
    godot: [
      { name: 'type', type: 'string', default: '', description: 'Object type (spawn_point, trigger, portal, npc, enemy, item)' },
      { name: 'script', type: 'string', default: '', description: 'Attached GDScript path' },
      { name: 'group', type: 'string', default: '', description: 'Node group name' },
    ],
    unity: [
      { name: 'type', type: 'string', default: '', description: 'Object type' },
      { name: 'unity:layer', type: 'string', default: 'Default', description: 'Unity layer' },
      { name: 'unity:tag', type: 'string', default: 'Untagged', description: 'Unity tag' },
      { name: 'unity:isTrigger', type: 'bool', default: false, description: 'Is trigger collider' },
      { name: 'prefab', type: 'string', default: '', description: 'Prefab to instantiate' },
    ],
    generic: [
      { name: 'type', type: 'string', default: '', description: 'Object type (spawn_point, trigger, portal, npc, enemy, item, zone, waypoint)' },
      { name: 'name', type: 'string', default: '', description: 'Object identifier' },
      { name: 'target', type: 'string', default: '', description: 'Target object (for links/portals)' },
      { name: 'enabled', type: 'bool', default: true, description: 'Active at map start' },
      { name: 'oneShot', type: 'bool', default: false, description: 'Trigger fires only once' },
    ],
  },

  // Layer properties
  layer: {
    phaser: [
      { name: 'depth', type: 'int', default: 0, description: 'Layer render depth' },
      { name: 'collision', type: 'bool', default: false, description: 'Entire layer has collision' },
    ],
    godot: [
      { name: 'z_index', type: 'int', default: 0, description: 'Layer Z index' },
      { name: 'y_sort', type: 'bool', default: false, description: 'Y-sort this layer' },
    ],
    unity: [
      { name: 'unity:SortingLayer', type: 'string', default: 'Default', description: 'Sorting layer name' },
      { name: 'unity:layer', type: 'string', default: 'Default', description: 'Physics layer' },
    ],
    generic: [],
  },

  // Map-level properties
  map: {
    phaser: [
      { name: 'background_color', type: 'color', default: '#000000', description: 'Map background color' },
    ],
    godot: [
      { name: 'background_color', type: 'color', default: '#000000', description: 'Map background' },
    ],
    unity: [],
    generic: [
      { name: 'music', type: 'string', default: '', description: 'Background music track' },
      { name: 'ambient', type: 'string', default: '', description: 'Ambient sound' },
      { name: 'gravity', type: 'float', default: 0, description: 'Gravity (platformers)' },
    ],
  },
};

/**
 * Map from EditorState targetEngine keys (e.g. 'phaser3', 'godot4', 'rpgmakerMZ')
 * to property preset keys (e.g. 'phaser', 'godot', 'rpgmaker').
 */
const ENGINE_KEY_MAP = {
  phaser3: 'phaser',
  godot4: 'godot',
  unity: 'unity',
  gamemaker: 'gamemaker',
  rpgmakerMZ: 'rpgmaker',
};

/**
 * Resolve an EditorState targetEngine value to a property preset key.
 * Falls back to 'generic' if no mapping exists.
 */
export function resolvePresetKey(targetEngine) {
  if (!targetEngine) return null;
  return ENGINE_KEY_MAP[targetEngine] || targetEngine;
}

/**
 * Get property presets for a given target type and engine.
 * @param {'tile'|'object'|'layer'|'map'} targetType
 * @param {string|null} engineKey - resolved preset key (use resolvePresetKey)
 * @returns {Array<{name, type, default, description}>}
 */
export function getPresets(targetType, engineKey) {
  const group = PROPERTY_PRESETS[targetType];
  if (!group) return [];
  return group[engineKey] || group.generic || [];
}

/**
 * All engine labels for the "Add Engine Properties" dropdown.
 */
export const ENGINE_LABELS = [
  { key: 'phaser', label: 'Phaser' },
  { key: 'godot', label: 'Godot' },
  { key: 'unity', label: 'Unity' },
  { key: 'gamemaker', label: 'GameMaker' },
  { key: 'rpgmaker', label: 'RPG Maker' },
  { key: 'generic', label: 'Generic' },
];
